import { contractMonthly as sharedContractMonthly } from "@/lib/contractMath";
// ============================================================================
// Administration — data layer (real FastAPI + MongoDB backend, Phase 3)
// ----------------------------------------------------------------------------
// Manager/admin scope (agency-wide). Backend-hydrated single store + optimistic
// mutations. Public proposal flow via token-gated endpoints (no auth). NO real
// email/PDF/e-signature — acceptance stores backend-ready evidence only.
// ============================================================================

import { getRegistryClient, updateRegistryClient } from "@/lib/clientRegistry";
import { api } from "@/lib/api";

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
// Token pubblico crittograficamente sicuro (SEC-001): mai timestamp-based.
const secureToken = () => {
  const a = new Uint8Array(24);
  window.crypto.getRandomValues(a);
  return `tok_${Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("")}`;
};
export const IVA = 0.22;
export const gross = (net) => Math.round(net * (1 + IVA) * 100) / 100;

export const PLAN_MONTHLY = { essential: 590, starter: 660, professional: 860, enterprise: 1460 };

// Listino ufficiale per durata (netto IVA esclusa). months -> €/mese.
export const PLAN_PRICING = {
  essential: { 3: 590 },
  starter: { 6: 825, 12: 660 },
  professional: { 6: 1075, 12: 860 },
  enterprise: { 6: 1825, 12: 1460 },
};
// Override configurabile da UI (Prodotti). Popolato dallo store; fallback alla costante.
let _planPricing = null;
export const getPlanPricing = () => _planPricing || PLAN_PRICING;
export const savePlanPricing = (matrix) => { _planPricing = matrix; touch(); return api.put("/administration/plan-pricing", { matrix }).catch(resync); };
export const planPriceByMonths = (planKey, months) => {
  const m = getPlanPricing()[planKey] || {};
  const v = m[months] ?? m[String(months)];
  return (v === undefined || v === null) ? null : Number(v);
};
export const planPrice = (planKey, durationLabel) => {
  const d = DURATIONS.find((x) => x.label === durationLabel);
  return d ? planPriceByMonths(planKey, d.months) : null;
};
// Ricava la chiave piano da un prodotto (per l'auto-prezzo quando aggiunto al preventivo).
export const productPlanKey = (product) => {
  const id = (product?.id || product?._id || "").toLowerCase();
  for (const k of ["essential", "starter", "professional", "enterprise"]) if (id.includes(k)) return k;
  const n = (product?.product_name || "").toLowerCase();
  if (n.includes("essential") || n.includes("attivazione sistema")) return "essential";
  if (n.includes("starter")) return "starter";
  if (n.includes("professional")) return "professional";
  if (n.includes("enterprise")) return "enterprise";
  return null;
};

// Catalogo piani ufficiali richiamabili nel Preventivatore/Contratti.
// I prezzi storici sui contratti/preventivi reali restano personalizzati (non toccati).
export const DURATIONS = [
  { key: "1m", label: "1 mese", months: 1 },
  { key: "3m", label: "3 mesi", months: 3 },
  { key: "6m", label: "6 mesi", months: 6 },
  { key: "1y", label: "1 anno", months: 12 },
];
export const PLAN_CATALOG = [
  { key: "essential", full_name: "Attivazione sistema 90 giorni", short_name: "Essential", monthly_net: 590, default_months: 3 },
  { key: "starter", full_name: "SocialPack Starter", short_name: "Starter", monthly_net: 660, default_months: 12 },
  { key: "professional", full_name: "SocialPack Professional", short_name: "Professional", monthly_net: 860, default_months: 12 },
  { key: "enterprise", full_name: "SocialPack Enterprise", short_name: "Enterprise", monthly_net: 1460, default_months: 12 },
];
// Altri prodotti a catalogo (oltre ai piani). monthly_net null = prezzo configurabile manualmente.
export const OTHER_PRODUCTS = [
  { key: "sito_web", short_name: "Sito Web" },
  { key: "delivery", short_name: "Delivery" },
  { key: "gestione_pratica", short_name: "Gestione Pratica" },
  { key: "abbonamento", short_name: "Abbonamento" },
  { key: "rinnovo_annuale", short_name: "Rinnovo Annuale" },
  { key: "altro", short_name: "Altro" },
];
// Opzioni piano per l'editor inline nei Contratti (rule 1). Compatibile con denominazioni storiche/custom.
export const CONTRACT_PLAN_OPTIONS = [
  ...PLAN_CATALOG.map((p) => ({ value: p.key, label: p.short_name })),
  ...OTHER_PRODUCTS.map((p) => ({ value: p.key, label: p.short_name })),
];

// ---- Motore sconti preventivo (netto IVA esclusa). Ordine di calcolo rule 20. ----
const _r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const lineListino = (it) => _r2((Number(it.unit_price) || 0) * (it.quantity || 1));
export const lineNet = (it) => _r2(lineListino(it) * (1 - (Number(it.discount_pct) || 0) / 100));
export const computeProposalTotals = (items = [], globalPct = 0) => {
  const listino = _r2(items.reduce((a, i) => a + lineListino(i), 0));
  const afterItems = _r2(items.reduce((a, i) => a + lineNet(i), 0));
  const itemSaving = _r2(listino - afterItems);
  const gp = Number(globalPct) || 0;
  const globalSaving = _r2(afterItems * gp / 100);
  const finalNet = _r2(afterItems - globalSaving);
  const totalSaving = _r2(listino - finalNet);
  const effectivePct = listino > 0 ? _r2(totalSaving / listino * 100) : 0;
  return { listino, afterItems, itemSaving, globalPct: gp, globalSaving, finalNet, totalSaving, effectivePct };
};
// Autopopolamento mensile contratto (rule 5): solo da voci contratto; niente invenzioni.
// Il valore contratto (totale/annuo) NON è una mensilità -> non usarlo come mensile.
export const contractMonthly = (c) => {
  // Fonte unica: mensilità dei prodotti attivi (lib/contractMath), aggiornata subito a ogni modifica.
  if ((c.included_products || []).length) {
    const m = sharedContractMonthly(c);
    return { value: m.monthlyNet, gross: m.monthlyGross, totalNet: m.totalNet, totalGross: m.totalGross, source: "products" };
  }
  if (c.monthly_amount != null && Number(c.monthly_amount) > 0) return { value: Number(c.monthly_amount), source: "set" };
  const itemsNet = (c.items || []).reduce((a, i) => a + (i.net_after != null ? Number(i.net_after) : lineListino(i)), 0);
  if (itemsNet > 0) return { value: _r2(itemsNet), source: "AUTO_POPULATED_HIGH_CONFIDENCE" };
  return { value: 0, source: "MANUAL_REQUIRED" };
};
export const getPlanCatalog = (key) => PLAN_CATALOG.find((p) => p.key === key) || null;
// Data fine = data inizio + N mesi (calendario reale).
export const addMonths = (isoDate, months) => {
  if (!isoDate || !months) return "";
  const d = new Date(isoDate);
  if (isNaN(d)) return "";
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(months));
  if (d.getDate() < day) d.setDate(0); // gestisce mesi più corti
  return d.toISOString().slice(0, 10);
};
export const PAYMENT_METHODS_QUOTE = ["Addebito C/C", "Addebito Carta", "Bonifico", "Altro"];
// Nuove modalità di pagamento raggruppate (redesign preventivi)
export const PAYMENT_MODALITIES = [
  "Addebito diretto c/c",
  "Addebito diretto su carta di credito/debito",
  "Bonifico a vista fattura",
];
export const BILLING_FREQUENCIES = ["Mensile", "Trimestrale", "Annuale", "Unica soluzione"];
export const durationMonths = (durLabel) => {
  const m = String(durLabel || "").toLowerCase().match(/(\d+)/);
  if (!m) return 0;
  const n = Number(m[1]);
  return /ann|year/.test(String(durLabel).toLowerCase()) ? n * 12 : n;
};

export const PROPOSAL_STATUS = {
  draft: { label: "Bozza", tone: "neutral" },
  sent: { label: "Inviata", tone: "info" },
  viewed: { label: "Visualizzata", tone: "info" },
  accepted: { label: "Accettata", tone: "ok" },
  rejected: { label: "Rifiutata", tone: "error" },
  expired: { label: "Scaduta", tone: "neutral" },
};
export const CONTRACT_STATUS = {
  active: { label: "Attivo", tone: "ok" },
  expiring: { label: "In scadenza", tone: "warning" },
  expired: { label: "Scaduto", tone: "error" },
  paused: { label: "Bloccato", tone: "warning" },
  terminated: { label: "Terminato", tone: "neutral" },
  renewal: { label: "Rinnovo in sospeso", tone: "info" },
  upsell: { label: "Opportunità upsell", tone: "info" },
};
// Raggruppamento nelle 3 sezioni della pagina Contratti.
export const CONTRACT_SECTION = (status) => {
  if (status === "paused") return "paused";
  if (status === "terminated") return "terminated";
  if (status === "expired") return "expired"; // scaduto: decide l'operatore (rinnova o termina), mai automatico
  return "active";
};
export const PAYMENT_METHODS = ["Bonifico bancario", "Addebito diretto (RID)", "Pagamento su fattura", "Altro"];
export const BILLING_PREFERENCES = ["1° del mese", "15 del mese", "Personalizzato"];
export const DEBIT_DAYS = ["1° del mese", "15 del mese"];
export const PRODUCT_CATEGORIES = ["Social", "Advertising", "Web", "CRM", "Video", "Consulenza", "Setup", "Altro"];

// ---------------------------------------------------------------------------
// CACHE (backend-hydrated single store)
// ---------------------------------------------------------------------------
let _store = { products: [], terms: [], templates: [], contracts: [], proposals: [], notifications: [], billing: {}, sendLogs: [], acceptanceLogs: [] };
let _hydrated = false;
let _hydratingPromise = null;
let _hydrateError = null;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeAdministration = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isAdministrationReady = () => _hydrated;
export const getAdministrationError = () => _hydrateError;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Carica il bundle Preventivi e Contratti. Se la richiesta fallisce (es. backend in riavvio durante un deploy)
// riprova con backoff e NON marca lo store come pronto: i dati non devono "sparire" silenziosamente.
export async function hydrateAdministration({ force = false } = {}) {
  if (_hydrated && !force) return;
  if (_hydratingPromise) return _hydratingPromise;
  _hydratingPromise = (async () => {
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/administration/store");
        if (data.planPricing && Object.keys(data.planPricing).length) _planPricing = data.planPricing;
        _store = { products: [], terms: [], templates: [], contracts: [], proposals: [], notifications: [], billing: {}, sendLogs: [], acceptanceLogs: [], ...data };
        _hydrated = true; _hydrateError = null; lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        const st = e?.response?.status;
        if (st === 401 || st === 403) break;
        await sleep(1500 * (attempt + 1));
      }
    }
    if (lastErr) {
      // eslint-disable-next-line no-console
      console.error("hydrateAdministration failed", lastErr?.response?.status, lastErr?.message);
      _hydrateError = lastErr?.response?.status || (/JSON|Unexpected token/i.test(lastErr?.message || "") ? "risposta non valida" : "network");
    }
    _hydratingPromise = null; notify();
  })();
  return _hydratingPromise;
}
export const resetAdministration = () => { _store = { products: [], terms: [], templates: [], contracts: [], proposals: [], notifications: [], billing: {}, sendLogs: [], acceptanceLogs: [] }; _hydrated = false; _hydrateError = null; _hydratingPromise = null; };
const resync = () => { hydrateAdministration({ force: true }); };
const touch = () => { _store = { ..._store }; notify(); };

// ---------------------------------------------------------------------------
// PRODUCTS
// ---------------------------------------------------------------------------
export const getProducts = () => _store.products;
export const addProduct = (d) => {
  const id = uid("prod"); _store.products = [..._store.products, { is_active: true, ...d, id }]; touch();
  api.post("/administration/products", { is_active: true, ...d, id }).then(({ data }) => { _store.products = _store.products.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync);
  return id;
};
export const updateProduct = (id, p) => { _store.products = _store.products.map((x) => (x.id === id ? { ...x, ...p } : x)); touch(); api.patch(`/administration/products/${id}`, p).catch(resync); };
export const removeProduct = (id) => { _store.products = _store.products.filter((x) => x.id !== id); touch(); api.delete(`/administration/products/${id}`).catch(resync); };

// ---------------------------------------------------------------------------
// INCLUDED SERVICES (Client Portal). Derivati da proposte accettate, fallback piano.
// ---------------------------------------------------------------------------
export const PLAN_INCLUDED_SERVICES = {
  starter: [
    { name: "Gestione campagne Meta Ads", quantity: "1 campagna / mese", description: "Setup, monitoraggio e ottimizzazione mensile.", status: "active" },
    { name: "Report mensile", quantity: "1 / mese", description: "Report performance con i risultati del mese.", status: "active" },
    { name: "Lead Manager", quantity: "Accesso incluso", description: "Gestione dei contatti generati dalle campagne.", status: "active" },
  ],
  essential: [
    { name: "Gestione campagne Meta Ads", quantity: "1 campagna / mese", description: "Setup, monitoraggio e ottimizzazione mensile.", status: "active" },
    { name: "Piano editoriale mensile", quantity: "1 / mese", description: "Calendario contenuti social del mese.", status: "active" },
    { name: "Report mensile", quantity: "1 / mese", description: "Report performance mensile.", status: "active" },
    { name: "Abbonamento WhatsApp", quantity: "1 numero attivo", description: "Automazione messaggio di benvenuto WhatsApp.", status: "active" },
    { name: "Lead Manager", quantity: "Accesso incluso", description: "Gestione dei contatti generati.", status: "active" },
  ],
  professional: [
    { name: "Gestione campagne Meta Ads", quantity: "2 campagne / mese", description: "Setup, monitoraggio e ottimizzazione mensile.", status: "active" },
    { name: "Piano editoriale mensile", quantity: "1 / mese", description: "Calendario contenuti social del mese.", status: "active" },
    { name: "Shooting", quantity: "4 / anno", description: "Sessioni di produzione foto/video incluse nell'accordo annuale.", status: "active" },
    { name: "Abbonamento WhatsApp", quantity: "1 numero attivo", description: "Automazione messaggio di benvenuto WhatsApp.", status: "active" },
    { name: "Report mensile", quantity: "1 / mese", description: "Report performance mensile.", status: "active" },
    { name: "Call di allineamento", quantity: "1 / mese", description: "Riunione mensile di allineamento con il team.", status: "active" },
    { name: "CRM & Lead Manager", quantity: "Accesso incluso", description: "Accesso alla piattaforma CRM e gestione lead.", status: "active" },
  ],
  enterprise: [
    { name: "Gestione campagne Meta Ads", quantity: "Campagne illimitate", description: "Gestione avanzata multi-campagna con strategia dedicata.", status: "active" },
    { name: "Piano editoriale mensile", quantity: "1 / mese", description: "Calendario contenuti premium.", status: "active" },
    { name: "Shooting", quantity: "12 / anno", description: "Sessioni di produzione foto/video mensili.", status: "active" },
    { name: "Landing page", quantity: "Inclusa", description: "Landing page ottimizzata per le campagne.", status: "active" },
    { name: "Abbonamento WhatsApp", quantity: "1 numero attivo", description: "Automazione WhatsApp.", status: "active" },
    { name: "Report mensile + Call", quantity: "1 / mese", description: "Report e call di allineamento mensili.", status: "active" },
    { name: "Ore di consulenza extra", quantity: "Su richiesta", description: "Consulenza strategica aggiuntiva inclusa.", status: "active" },
    { name: "CRM & Lead Manager", quantity: "Accesso incluso", description: "Accesso completo alla piattaforma.", status: "active" },
  ],
};

export const getIncludedServices = (clientId) => {
  const accepted = _store.proposals.filter((p) => p.client_id === clientId && p.status === "accepted");
  const items = [];
  accepted.forEach((p) => (p.items || []).forEach((it) => {
    const prod = _store.products.find((x) => x.id === it.product_id);
    items.push({ id: it.id, name: it.product_name, quantity: it.quantity > 1 ? `${it.quantity} × ${prod?.default_duration || "incluso"}` : (prod?.default_duration || "Incluso"), description: it.description || prod?.description || "", status: "active" });
  }));
  if (items.length) return items;
  const plan = getRegistryClient(clientId)?.plan || "starter";
  return (PLAN_INCLUDED_SERVICES[plan] || PLAN_INCLUDED_SERVICES.starter).map((x, i) => ({ id: `plan_${plan}_${i}`, ...x }));
};

// ---------------------------------------------------------------------------
// TERMS
// ---------------------------------------------------------------------------
export const getTermsTemplates = () => _store.terms;
export const addTerms = (d) => { const id = uid("terms"); _store.terms = [..._store.terms, { is_active: true, updated_at: now(), ...d, id }]; touch(); api.post("/administration/terms", { is_active: true, ...d, id }).then(({ data }) => { _store.terms = _store.terms.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync); return id; };
export const updateTerms = (id, p) => { _store.terms = _store.terms.map((x) => (x.id === id ? { ...x, ...p, updated_at: now() } : x)); touch(); api.patch(`/administration/terms/${id}`, p).catch(resync); };
export const removeTerms = (id) => { _store.terms = _store.terms.filter((x) => x.id !== id); touch(); api.delete(`/administration/terms/${id}`).catch(resync); };

// ---------------------------------------------------------------------------
// PROPOSAL TEMPLATES
// ---------------------------------------------------------------------------
export const getProposalTemplates = () => _store.templates;
export const getProposalTemplate = (id) => _store.templates.find((x) => x.id === id) || null;
export const addProposalTemplate = (d) => { const id = uid("tpl"); _store.templates = [..._store.templates, { is_active: true, updated_at: now(), ...d, id }]; touch(); api.post("/administration/templates", { is_active: true, ...d, id }).then(({ data }) => { _store.templates = _store.templates.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync); return id; };
export const updateProposalTemplate = (id, p) => { _store.templates = _store.templates.map((x) => (x.id === id ? { ...x, ...p, updated_at: now() } : x)); touch(); api.patch(`/administration/templates/${id}`, p).catch(resync); };
export const removeProposalTemplate = (id) => { _store.templates = _store.templates.filter((x) => x.id !== id); touch(); api.delete(`/administration/templates/${id}`).catch(resync); };

// ---------------------------------------------------------------------------
// PROPOSALS
// ---------------------------------------------------------------------------
export const getProposals = () => _store.proposals.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
export const getProposal = (id) => _store.proposals.find((x) => x.id === id) || null;
export const resolveProposalToken = (token) => _store.proposals.find((x) => x.proposal_token === token) || null;
export const addProposal = (d, author) => {
  const id = uid("prop"); const token = secureToken();
  const p = { id, status: "draft", items: [], created_by: author || "Operatore", created_at: now(), updated_at: now(), sent_at: null, viewed_at: null, accepted_at: null, rejected_at: null, ...d, proposal_token: token };
  _store.proposals = [..._store.proposals, p]; touch();
  api.post("/administration/proposals", { ...d, id, proposal_token: token }).then(({ data }) => { _store.proposals = _store.proposals.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync);
  return id;
};
export const updateProposal = (id, p) => { _store.proposals = _store.proposals.map((x) => (x.id === id ? { ...x, ...p, updated_at: now() } : x)); touch(); api.patch(`/administration/proposals/${id}`, p).catch(resync); };
export const removeProposal = (id) => { _store.proposals = _store.proposals.filter((x) => x.id !== id); touch(); api.delete(`/administration/proposals/${id}`).catch(resync); };
export const sendProposal = (id, { email_message, whatsapp_message, channel, sent_to } = {}) => {
  _store.proposals = _store.proposals.map((x) => (x.id === id ? { ...x, status: x.status === "accepted" ? "accepted" : "sent", sent_at: now(), email_message, whatsapp_message, updated_at: now() } : x)); touch();
  api.post(`/administration/proposals/${id}/send`, { email_message, whatsapp_message, channels: { email: true, whatsapp: false }, email_to: sent_to }).catch(resync);
};
// Invio REALE con canali selezionabili: ritorna i risultati per canale.
export const sendProposalReal = async (id, payload) => {
  const { data } = await api.post(`/administration/proposals/${id}/send`, payload);
  if (data?.proposal) { _store.proposals = _store.proposals.map((x) => (x.id === id ? data.proposal : x)); touch(); }
  return data;
};

// ---------------------------------------------------------------------------
// CONTRACTS
// ---------------------------------------------------------------------------
export const getContracts = () => _store.contracts;
export const getContract = (id) => _store.contracts.find((x) => x.id === id) || null;
export const getContractByClient = (clientId) => _store.contracts.find((x) => x.client_id === clientId) || null;
export const addContract = (d) => { const id = uid("ct"); _store.contracts = [..._store.contracts, { id, created_at: now(), updated_at: now(), ...d }]; touch(); api.post("/administration/contracts", { id, ...d }).then(({ data }) => { _store.contracts = _store.contracts.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync); return id; };
// Creazione manuale atomica: crea il contratto e i prodotti, poi ricarica lo store.
export const createContractWithProducts = async ({ client_id, acceptance_date, contract_start, contract_end, products }) => {
  const { data } = await api.post("/administration/contracts", { client_id, acceptance_date, contract_start, contract_end });
  const id = data.id || data._id;
  for (const p of (products || [])) {
    await api.post(`/administration/contracts/${id}/products`, p);
  }
  await hydrateAdministration();
  return id;
};
// Import massivo contratti da CSV (una riga per prodotto). Ritorna il report backend.
export const importContractsCsv = async (rows) => {
  const { data } = await api.post("/administration/contracts/import-csv", { rows });
  await hydrateAdministration();
  return data;
};

export const updateContract = (id, p) => { _store.contracts = _store.contracts.map((x) => (x.id === id ? { ...x, ...p, updated_at: now() } : x)); touch(); api.patch(`/administration/contracts/${id}`, p).catch(resync); };
export const deleteContract = async (id) => {
  const { data } = await api.delete(`/administration/contracts/${id}`);
  _store.contracts = _store.contracts.filter((x) => x.id !== id);
  touch();
  return data;
};

// Fine usata per lo stato (identica al backend): scadenza più recente dei prodotti attivi;
// se tutti scaduti in automatico, la loro scadenza; altrimenti contract_end.
const _CLOSED_P = ["removed", "terminated", "cancelled"];
export const contractStatusEnd = (ct) => {
  const prods = (ct.included_products || []).filter((p) => (p.status || "active") !== "removed");
  const act = prods.filter((p) => !_CLOSED_P.includes(p.status || "active") && p.end_date).map((p) => String(p.end_date).slice(0, 10));
  if (act.length) return act.sort()[act.length - 1];
  const exp = prods.filter((p) => p.auto_expired && p.end_date).map((p) => String(p.end_date).slice(0, 10));
  const openUndated = prods.some((p) => !p.end_date && !_CLOSED_P.includes(p.status || "active"));
  if (exp.length && !openUndated) return exp.sort()[exp.length - 1];
  return ct.contract_end ? String(ct.contract_end).slice(0, 10) : null;
};
export const contractStatus = (ct) => {
  const ov = ct.status_override;
  if (ov === "paused") return "paused";
  if (ov === "terminated" || ov === "ended" || ov === "cancelled") return "terminated";
  const live = (ct.included_products || []).filter((p) => (p.status || "active") !== "removed");
  if (live.length && live.every((p) => _CLOSED_P.includes(p.status || "active"))) return "terminated";
  if (ov) return ov;
  const endIso = contractStatusEnd(ct);
  if (!endIso) return "active";
  const days = Math.round((new Date(endIso + "T00:00:00") - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (isNaN(days)) return "active";
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "active";
};

// ---- Contract lifecycle (backend-audited) ----
const _applyContract = (id, data) => { _store.contracts = _store.contracts.map((x) => (x.id === id ? data : x)); touch(); };
export const patchContractInline = (id, patch) => {
  _store.contracts = _store.contracts.map((x) => (x.id === id ? { ...x, ...patch, updated_at: now() } : x)); touch();
  return api.patch(`/administration/contracts/${id}`, patch).then(({ data }) => _applyContract(id, data)).catch(resync);
};
export const renewContract = (id, body) => api.post(`/administration/contracts/${id}/renew`, body).then(({ data }) => { _applyContract(id, data); return data; }).catch((e) => { resync(); throw e; });
export const extendContractApi = (id, body) => api.post(`/administration/contracts/${id}/extend`, body).then(({ data }) => { _applyContract(id, data); return data; }).catch((e) => { resync(); throw e; });
export const pauseContract = (id, reasons, note) => api.post(`/administration/contracts/${id}/pause`, { reasons, note }).then(({ data }) => _applyContract(id, data)).catch((e) => { resync(); throw e; });
export const reactivateContract = (id) => api.post(`/administration/contracts/${id}/reactivate`).then(({ data }) => _applyContract(id, data)).catch(resync);
export const terminateContract = (id, reasons, note) => api.post(`/administration/contracts/${id}/terminate`, { reasons, note }).then(({ data }) => _applyContract(id, data)).catch((e) => { resync(); throw e; });

// ---- Contract documents (object storage) ----
export const listContractDocuments = (id) => api.get(`/administration/contracts/${id}/documents`).then((r) => r.data);
export const uploadContractDocument = (id, file) => {
  const fd = new FormData(); fd.append("file", file);
  return api.post(`/administration/contracts/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const contractDocumentUrl = (id, docId) => `${process.env.REACT_APP_BACKEND_URL}/api/administration/contracts/${id}/documents/${docId}/download`;
export const openContractDocument = (id, docId) =>
  api.get(`/administration/contracts/${id}/documents/${docId}/download`, { responseType: "blob" })
    .then((r) => { const url = URL.createObjectURL(r.data); window.open(url, "_blank", "noopener"); });
export const deleteContractDocument = (id, docId) => api.delete(`/administration/contracts/${id}/documents/${docId}`).then((r) => r.data);

// (Ri)invio email al cliente con il PDF del contratto firmato (mittente noreply@aestheticdigital.it)
export const resendContractEmail = (id) => api.post(`/administration/contracts/${id}/resend-email`).then(({ data }) => { resync(); return data; });
export const emailContractDocument = (id, docId) => api.post(`/administration/contracts/${id}/documents/${docId}/email`).then(({ data }) => { resync(); return data; });
export const generateContractDocument = (id) => api.post(`/administration/contracts/${id}/documents/generate`).then((r) => r.data);
export const getStopReasons = () => api.get("/administration/contracts/stop-reasons").then(({ data }) => data.reasons || []);

// ---- Contract included products (catalog-linked, backend-audited) ----
export const addContractProduct = (id, body) => api.post(`/administration/contracts/${id}/products`, body).then(({ data }) => { _applyContract(id, data); return data; }).catch((e) => { resync(); throw e; });
export const updateContractProduct = (id, lineId, body) => api.patch(`/administration/contracts/${id}/products/${lineId}`, body).then(({ data }) => { _applyContract(id, data); return data; }).catch((e) => { resync(); throw e; });
export const removeContractProduct = (id, lineId, reason) => api.delete(`/administration/contracts/${id}/products/${lineId}`, { params: reason ? { reason } : {} }).then(({ data }) => { _applyContract(id, data); return data; }).catch((e) => { resync(); throw e; });

// ---------------------------------------------------------------------------
// BILLING
// ---------------------------------------------------------------------------
export const getBilling = (clientId) => _store.billing[clientId] || null;
export const updateBilling = (clientId, p) => { _store.billing = { ..._store.billing, [clientId]: { ...(_store.billing[clientId] || { client_id: clientId }), ...p, updated_at: now() } }; touch(); api.patch(`/administration/billing/${clientId}`, p).catch(resync); };

// ---------------------------------------------------------------------------
// NOTIFICATIONS
// ---------------------------------------------------------------------------
export const getNotifications = () => _store.notifications.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
export const addNotification = (d) => { const id = uid("notif"); _store.notifications = [{ id, status: "unread", created_at: now(), read_at: null, ...d }, ..._store.notifications]; touch(); api.post("/administration/notifications", d).then(({ data }) => { _store.notifications = _store.notifications.map((x) => (x.id === id ? data : x)); touch(); }).catch(resync); };
export const markNotificationRead = (id) => { _store.notifications = _store.notifications.map((x) => (x.id === id ? { ...x, status: "read", read_at: now() } : x)); touch(); api.patch(`/administration/notifications/${id}/read`).catch(resync); };

// SEND LOGS + ACCEPTANCE LOGS
export const getSendLogs = (proposalId) => _store.sendLogs.filter((x) => !proposalId || x.proposal_id === proposalId);
export const getAcceptanceLog = (proposalId) => _store.acceptanceLogs.find((x) => x.proposal_id === proposalId) || null;
export const getAcceptanceLogByClient = (clientId) => _store.acceptanceLogs.filter((x) => x.client_id === clientId);

// ---------------------------------------------------------------------------
// PUBLIC PROPOSAL FLOW (token-gated, no auth) — pagina pubblica
// ---------------------------------------------------------------------------
const detectClient = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const device = /Mobi|Android|iPhone|iPad/i.test(ua) ? (/iPad|Tablet/i.test(ua) ? "Tablet" : "Mobile") : "Desktop";
  const browser = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "Sconosciuto";
  const os = /Windows/i.test(ua) ? "Windows" : /Mac OS/i.test(ua) ? "macOS" : /Android/i.test(ua) ? "Android" : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Linux/i.test(ua) ? "Linux" : "Sconosciuto";
  return { user_agent: ua, device_type: device, browser, operating_system: os };
};

export const fetchPublicProposal = (token) => api.get(`/administration/public/proposal/${token}`).then((r) => r.data);

export const acceptProposalPublic = (token, { billing, signatureDataUrl, accepted, preferred_start, payment }) => {
  const info = detectClient();
  return api.post(`/administration/public/proposal/${token}/accept`, {
    billing, signatureDataUrl, accepted, preferred_start, payment, device_type: info.device_type, browser: info.browser, operating_system: info.operating_system,
  }).then((r) => r.data);
};

// Internal orchestration (cache aggiornata; usato raramente da flussi interni).
export const acceptProposal = async (proposalId, { billing, signatureDataUrl, accepted }) => {
  const prop = getProposal(proposalId);
  if (!prop) return null;
  const res = await acceptProposalPublic(prop.proposal_token, { billing, signatureDataUrl, accepted });
  updateRegistryClient(prop.client_id, {
    company_name: billing.company_name, vat_number: billing.vat_number, fiscal_code: billing.fiscal_code,
    sdi_code: billing.sdi_code, iban: billing.iban, address: billing.registered_office_address,
    city: billing.city, province: billing.province, zip_code: billing.zip_code,
    main_contact_name: billing.main_contact_name, main_contact_email: billing.main_contact_email, main_contact_phone: billing.main_contact_phone,
  });
  hydrateAdministration();
  return res?.contract_id || null;
};

// Manual acceptance by authenticated operator (proposta firmata offline / a voce / via email).
// Il backend riusa la stessa logica del flusso pubblico: crea/riusa cliente per P.IVA/email,
// crea o appende contratto, genera posizioni contabili, upsert contact CRM. Salta Stripe.
export const acceptProposalManual = async (proposalId, opts = {}) => {
  const { data } = await api.post(`/administration/proposals/${proposalId}/accept-manual`, opts || {});
  await hydrateAdministration();
  return data?.contract_id || null;
};

// ---------------------------------------------------------------------------
// KPI DASHBOARD
// ---------------------------------------------------------------------------
const proposalValue = (p) => Number(p.amount) ||
  (p.items || []).reduce((a, it) => a + (Number(it.total_price ?? it.net_after) || 0), 0);

// KPI dashboard Preventivi e Contratti — dipendenti dal periodo selezionato (mese/anno).
export const adminKpis = (period) => {
  const now = new Date();
  const year = period?.year ?? now.getFullYear();
  const month = period?.month ?? now.getMonth() + 1;
  const prevY = month === 1 ? year - 1 : year;
  const prevM = month === 1 ? 12 : month - 1;
  const inMonth = (iso, y, m) => { if (!iso) return false; const d = new Date(iso); return d.getFullYear() === y && d.getMonth() + 1 === m; };

  const contracts = _store.contracts;
  const statuses = contracts.map(contractStatus);
  const isActive = (x) => ["active", "expiring", "renewal", "upsell"].includes(x);
  const active = statuses.filter(isActive).length;
  const expiring = statuses.filter((x) => x === "expiring").length;
  const mrr = contracts.filter((c, i) => isActive(statuses[i])).reduce((a, c) => a + (contractMonthly(c).value || 0), 0);
  const pendingSign = _store.proposals.filter((p) => ["sent", "viewed"].includes(p.status)).length;
  // Voucher in corso: contratti attivi collegati a un prodotto/titolo "voucher"
  const vouchers = contracts.filter((c, i) => isActive(statuses[i]) &&
    (/voucher/i.test(c.title || "") || (c.included_products || []).some((p) => /voucher/i.test(p.product_name || p.name || "")))).length;
  const acceptedValue = (y, m) => _store.proposals
    .filter((p) => p.status === "accepted" && inMonth(p.accepted_at, y, m))
    .reduce((a, p) => a + proposalValue(p), 0);
  const acceptedMonth = acceptedValue(year, month);
  const acceptedPrev = acceptedValue(prevY, prevM);
  const deltaPct = acceptedPrev > 0 ? ((acceptedMonth - acceptedPrev) / acceptedPrev) * 100 : null;
  const renewedUpsell = _store.proposals.filter((p) => p.status === "accepted" &&
    inMonth(p.accepted_at, year, month) && /^(rinnovo|upsell)/i.test(p.title || "")).length;
  // % conversione voucher: clienti che hanno avuto PRIMA un contratto voucher e POI un
  // contratto con un prodotto diverso (non voucher), sul totale dei clienti con voucher.
  const isVoucherC = (c) => /voucher/i.test(c.title || "") ||
    (c.included_products || []).some((p) => /voucher/i.test(p.product_name || p.name || ""));
  const cStart = (c) => new Date(c.start_date || c.created_at || 0).getTime();
  const byClient = {};
  contracts.forEach((c) => { if (c.client_id) (byClient[c.client_id] = byClient[c.client_id] || []).push(c); });
  let voucherClients = 0, voucherConverted = 0;
  Object.values(byClient).forEach((list) => {
    const vs = list.filter(isVoucherC);
    if (!vs.length) return;
    voucherClients += 1;
    const firstVoucher = Math.min(...vs.map(cStart));
    const laterOther = list.some((c) => !isVoucherC(c) && cStart(c) >= firstVoucher &&
      (c.included_products || []).some((p) => !/voucher/i.test(p.product_name || p.name || "")));
    if (laterOther) voucherConverted += 1;
  });
  const voucherConversionPct = voucherClients ? Math.round((voucherConverted / voucherClients) * 100) : 0;
  return { pendingSign, vouchers, acceptedMonth, acceptedPrev, deltaPct, active, expiring, renewedUpsell, mrr,
    voucherConversionPct, voucherClients, voucherConverted };
};
